/**
 * The settings screen, built to the board.
 *
 * Four sections in the order Figma sets them — ПРОФИЛЬ, УВЕДОМЛЕНИЯ, АУДИО,
 * КОНТЕНТ — each a centred heading over rows drawn as pills.
 *
 * Every row is a real button and every row looks the same. An earlier pass
 * dimmed the ones whose systems are not built yet, which was not in the design
 * and made two thirds of the screen read as broken. Where a row has nothing
 * behind it, it says «скоро» when tapped rather than looking disabled or
 * silently doing nothing.
 */
import { useEffect, useState } from 'react';
import { Settings as EffectsSheet } from './Settings.js';
import { BottomNav } from './BottomNav.js';
import { Dialog } from './Dialog.js';
import { PasswordChange } from './PasswordChange.js';
import type { Prefs } from './prefs.js';

interface Props {
  prefs: Prefs;
  onChange: (next: Prefs) => void;
  onClose: () => void;
  lang: 'ru' | 'en';
  onLang: () => void;
  onShop: () => void;
  onProfile: () => void;
}

const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 12H5m6-6-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/*
 * The row icons are Marina's, pulled out of the board as SVG and used as masks
 * so they take the row's colour. They used to be twelve paths I drew from the
 * row names — a bell for «Обновление новелл» where the board has an open book,
 * a price tag where it has a SALE rosette, an eye where it has a crossed
 * circle. Close enough to describe, not close enough to ship.
 */
type Glyph =
  | 'link' | 'key' | 'exit'
  | 'book' | 'sale' | 'coins'
  | 'volume' | 'music' | 'sparkle'
  | 'globe' | 'censor' | 'trash';

const Icon = ({ name }: { name: Glyph }) => (
  <span className={`set-ico ico-${name}`} aria-hidden="true" />
);

export function SettingsScreen({ prefs, onChange, onClose, lang, onLang, onShop, onProfile }: Props) {
  const [effectsOpen, setEffectsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [cacheOpen, setCacheOpen] = useState(false);
  const [pct, setPct] = useState(0);
  const [passwordOpen, setPasswordOpen] = useState(false);
  /** Row id → transient note shown in place of a value. */
  const [note, setNote] = useState<Record<string, string>>({});

  const say = (id: string, text: string) => {
    setNote((n) => ({ ...n, [id]: text }));
    window.setTimeout(() => setNote((n) => { const { [id]: _drop, ...rest } = n; return rest; }), 2200);
  };

  /** A row the design specifies but this build has no system behind yet. */
  const Soon = ({ id, glyph, label, extra }: { id: string; glyph: Glyph; label: string; extra?: React.ReactNode }) => (
    <button className="set-row" onClick={() => say(id, 'скоро')}>
      <span className="set-row-icon"><Icon name={glyph} /></span>
      <span className="set-row-label">{label}</span>
      {note[id] ? <span className="set-note">{note[id]}</span> : extra}
    </button>
  );

  /** A row with a real switch, which is what the board draws on six of them. */
  const Toggle = ({ glyph, label, on, set }: { glyph: Glyph; label: string; on: boolean; set: (v: boolean) => void }) => (
    <button className="set-row" aria-pressed={on} onClick={() => set(!on)}>
      <span className="set-row-icon"><Icon name={glyph} /></span>
      <span className="set-row-label">{label}</span>
      <span className="set-switch" />
    </button>
  );

  const clearCache = () => {
    try {
      const keep = ['hon.save', 'hon.prefs'];
      for (const k of Object.keys(localStorage)) if (!keep.includes(k)) localStorage.removeItem(k);
      caches?.keys().then((ks) => ks.forEach((k) => caches.delete(k))).catch(() => {});
    } catch { /* private mode — nothing cached to clear */ }
    setPct(0);
    setCacheOpen(true);
  };

  /*
   * The board draws a determinate bar and a «39%» under it, so the dialog holds
   * for the length of the bar rather than flickering — clearing a few keys is
   * instant. The number is the bar's own position and nothing else; it is not
   * pretending to measure work.
   */
  useEffect(() => {
    if (!cacheOpen) return;
    const started = performance.now();
    const id = window.setInterval(() => {
      const done = Math.min(1, (performance.now() - started) / 2000);
      setPct(Math.round(done * 100));
      if (done === 1) { window.clearInterval(id); setCacheOpen(false); }
    }, 60);
    return () => window.clearInterval(id);
  }, [cacheOpen]);

  /*
   * The board draws Спецэффекты as a plain on/off switch, but the effects
   * setting has three levels because the middle one is the photosensitivity
   * accommodation. The switch turns effects off and back on to whatever level
   * was last chosen; the levels themselves live behind a long press, which is
   * also where text speed and size live — neither has a row in the design and
   * neither is something this game can ship without.
   */
  const effectsOn = prefs.effects !== 'off';
  const toggleEffects = () =>
    onChange({ ...prefs, effects: effectsOn ? 'off' : (prefs.seenWarning ? 'full' : 'reduced') });

  return (
    <div className="settings" role="dialog" aria-modal="true" aria-label="Настройки">
      <button className="settings-back" onClick={onClose} aria-label="Назад"><Arrow /></button>

      <div className="settings-sections">
        <section className="set-section">
          <h2 className="set-heading">ПРОФИЛЬ</h2>
          <Soon
            id="accounts" glyph="link" label="Связанные аккаунты"
            extra={<span className="set-socials" aria-hidden="true"><span className="set-social is-fb" /><span className="set-social is-tg" /></span>}
          />
          <button className="set-row" onClick={() => setPasswordOpen(true)}>
            <span className="set-row-icon"><Icon name="key" /></span>
            <span className="set-row-label">Сменить пароль</span>
          </button>
          <Soon id="logout" glyph="exit" label="Выйти из аккаунта" />
        </section>

        <section className="set-section">
          <h2 className="set-heading">УВЕДОМЛЕНИЯ</h2>
          <Toggle glyph="book" label="Обновление новелл"
            on={prefs.notifyNovels} set={(v) => onChange({ ...prefs, notifyNovels: v })} />
          <Toggle glyph="sale" label="Предложения"
            on={prefs.notifyOffers} set={(v) => onChange({ ...prefs, notifyOffers: v })} />
          <Toggle glyph="coins" label="Пополнение валюты"
            on={prefs.notifyCurrency} set={(v) => onChange({ ...prefs, notifyCurrency: v })} />
        </section>

        <section className="set-section">
          <h2 className="set-heading">АУДИО</h2>

          <div className="set-row">
            <span className="set-row-icon"><Icon name="volume" /></span>
            <span className="set-row-label">Громкость</span>
            <input
              className="set-slider"
              type="range" min={0} max={100} step={5}
              value={prefs.volume}
              aria-label="Громкость"
              onChange={(e) => onChange({ ...prefs, volume: Number(e.target.value) })}
            />
            <span className="set-row-value">{prefs.volume}&nbsp;%</span>
          </div>

          <button
            className="set-row" aria-pressed={prefs.music}
            onClick={() => onChange({ ...prefs, music: !prefs.music })}
          >
            <span className="set-row-icon"><Icon name="music" /></span>
            <span className="set-row-label">Музыка</span>
            <span className="set-switch" />
          </button>

          <button
            className="set-row" aria-pressed={effectsOn}
            onClick={toggleEffects}
            onContextMenu={(e) => { e.preventDefault(); setEffectsOpen(true); }}
          >
            <span className="set-row-icon"><Icon name="sparkle" /></span>
            <span className="set-row-label">Спецэффекты</span>
            <span className="set-switch" />
          </button>
        </section>

        <section className="set-section">
          <h2 className="set-heading">КОНТЕНТ</h2>

          <button className="set-row" onClick={onLang}>
            <span className="set-row-icon"><Icon name="globe" /></span>
            <span className="set-row-label">Язык</span>
            <span className="set-row-value">{lang === 'ru' ? 'Русский' : 'English'}</span>
            <span className="set-chevron" aria-hidden="true">›</span>
          </button>

          <button
            className="set-row" aria-pressed={prefs.censor}
            onClick={() => onChange({ ...prefs, censor: !prefs.censor })}
          >
            <span className="set-row-icon"><Icon name="censor" /></span>
            <span className="set-row-label">Цензура</span>
            <span
              className="set-help" role="button" tabIndex={0}
              onClick={(e) => { e.stopPropagation(); setHelpOpen(true); }}
            >?</span>
            <span className="set-switch" />
          </button>


          <button className="set-row" onClick={clearCache}>
            <span className="set-row-icon"><Icon name="trash" /></span>
            <span className="set-row-label">Очистить кэш</span>
            {note['cache'] && <span className="set-note">{note['cache']}</span>}
          </button>
        </section>
      </div>

      <BottomNav current="settings" lang={lang} onShop={onShop} onProfile={onProfile} onSettings={() => {}} />

      {helpOpen && (
        <Dialog badge="!" title="ЦЕНЗУРА" label="Цензура" onClose={() => setHelpOpen(false)}>
          <p className="dlg-body">
            Скрывает нецензурную лексику, анатомические подробности и сцены жестокого
            насилия. Если подобные элементы необходимы для сюжета, перед их появлением
            вы получите предупреждение и сможете отказаться от просмотра сцены.
          </p>
        </Dialog>
      )}

      {cacheOpen && (
        <Dialog
          badge="!"
          title={`ПОЖАЛУЙСТА
ПОДОЖДИТЕ!`}
          label="Очистка кэша"
          onClose={() => setCacheOpen(false)}
          outside={
            <>
              <span className="dlg-chibi" aria-hidden="true" />
              <p className="dlg-sub">Кэш очищается. Пока можете потрогать траву&nbsp;;)</p>
              <div
                className="dlg-bar"
                role="progressbar"
                aria-label="Очистка кэша"
                aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
              >
                <i style={{ width: `${pct}%` }} />
              </div>
              <p className="dlg-pct">{pct}%</p>
            </>
          }
        />
      )}

      {passwordOpen && <PasswordChange onDone={() => setPasswordOpen(false)} />}

      {effectsOpen && (
        <EffectsSheet prefs={prefs} onChange={onChange} onClose={() => setEffectsOpen(false)} />
      )}
    </div>
  );
}
